
import React, { ReactNode } from 'react';
import { motion } from 'framer-motion';
import FilterDropdown, { FilterOption } from '@/components/ui/FilterDropdown';
import { cn } from '@/lib/utils';
import DashboardButton from '@/components/ui/DashboardButton';

interface PageActionsProps {
  filterSection?: ReactNode;
  filterOptions?: {
    options: FilterOption[];
    onFilterChange: (updatedOptions: FilterOption[]) => void;
    label?: string;
  };
  actionButton?: {
    label: string;
    icon?: ReactNode;
    onClick?: () => void;
    'data-test-id'?: string;
  };
  className?: string;
}

const pageAnimationVariants = {
  hidden: { 
    opacity: 0,
    y: -10,
  },
  visible: { 
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1.0],
      staggerChildren: 0.1,
    }
  }
};

const itemAnimationVariants = {
  hidden: { opacity: 0, y: -8 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" }
  }
};

// Completely removed the search functionality from PageActions
const PageActions: React.FC<PageActionsProps> = ({
  filterSection,
  filterOptions,
  actionButton,
  className
}) => {
  // If no filter options or action button, don't render anything
  if (!filterOptions && !actionButton && !filterSection) {
    return null;
  }
  
  return (
    <motion.div 
      className={cn("flex flex-col md:flex-row gap-4 mb-4", className)}
      initial="hidden"
      animate="visible"
      variants={pageAnimationVariants}
    >
      {filterOptions && (
        <motion.div className="flex-shrink-0" variants={itemAnimationVariants}>
          <FilterDropdown 
            options={filterOptions.options}
            onFilterChange={filterOptions.onFilterChange}
            label={filterOptions.label}
          />
        </motion.div>
      )}
      
      {filterSection && (
        <motion.div className="flex flex-wrap gap-2" variants={itemAnimationVariants}>
          {filterSection}
        </motion.div>
      )}
      
      {actionButton && (
        <motion.div className="flex-shrink-0" variants={itemAnimationVariants}>
          <DashboardButton 
            onClick={actionButton.onClick}
            className="whitespace-nowrap"
            {...(actionButton['data-test-id'] ? { 'data-test-id': actionButton['data-test-id'] } : {})}
          >
            {actionButton.icon && (
              <span className="mr-2">{actionButton.icon}</span>
            )}
            {actionButton.label}
          </DashboardButton>
        </motion.div>
      )}
    </motion.div>
  );
};

export default PageActions;
